import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { NewDeviceDialog } from '@/features/inventory/DeviceDialogs';
import { inventoryErrorKey } from '@/features/inventory/inventoryErrors';
import { Caution } from '@/features/inventory/registerUi';
import { isReadyToInstall, useDevices, type AdminDevice } from '@/features/inventory/useInventory';
import type { AdminTenant } from '@/features/tenants/useTenants';
import { InstallationPanel } from '@/features/installations/InstallationPanel';
import { GoLivePanel } from './GoLivePanel';
import { useProvisionVehicle } from './useProvisioning';
import {
  draftToBody,
  EMPTY_DRAFT,
  Field,
  HardwareFields,
  IdentityFields,
  INPUT,
  PlanField,
  type Draft,
} from './vehicleFields';

/**
 * The provisioning wizard — canvas `5g`, four steps.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * 🔴 STEP 4 IS THE PRODUCT, NOT THE FORM.
 * ─────────────────────────────────────────────────────────────────────────────
 * Steps 1–3 collect what an installer knows. Step 4 is the go-live gate: the
 * vehicle is ALREADY provisioned by then and deliberately invisible to the
 * customer, and it stays invisible until the checks pass. `5g`'s rule — "no
 * half-installed devices in client fleets" — is the whole reason the wizard has
 * a fourth step instead of ending at Save.
 *
 * The tick marks are advisory. **The server re-runs every check when Go live is
 * pressed** and refuses on its own verdict; a screen showing three green ticks
 * is not evidence, because the device may have stopped reporting since they were
 * fetched.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * 🔴 SLICE 18d — THE IMEI FIELD AND THE SIM FIELDS ARE GONE. NOT RENAMED, GONE.
 * ─────────────────────────────────────────────────────────────────────────────
 * Step 1 used to take a typed IMEI and step 3 took a typed SIM number and
 * carrier. Both were a SECOND REPRESENTATION of something the registers now own,
 * and the two representations were free to disagree: a typo created a Traccar
 * device for hardware nobody holds, and a SIM number lived on a vehicle row
 * where no stock count could ever see it.
 *
 * So step 1 is a PICKER over the device register and step 3 is READ-ONLY,
 * resolved from whatever SIM is actually fitted to the chosen device. There is
 * no free-text SIM input anywhere in this file, and the request body has no
 * `simMsisdn` field to put one in.
 *
 * 🔴 A DEVICE WHOSE SIM IS NOT ACTIVATED STILL APPEARS IN THE PICKER, WITH A
 * WARNING. Hiding it would be the worse failure: an installer holding that exact
 * box would find it missing from the list with no explanation and would go
 * around the system. The same reasoning gives step 1 an inline "register a
 * device" escape hatch — an unregistered box in someone's hand is a fact, and a
 * wizard with no path forward from it is how the register starts being wrong.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * 🔴 SLICE 18f — THE FIELDS AND STEP 4 NOW LIVE OUTSIDE THIS FILE.
 * ─────────────────────────────────────────────────────────────────────────────
 * `vehicleFields.tsx` owns every field, option list and body mapping, and
 * `GoLivePanel.tsx` owns step 4. There are now THREE entrances to go-live — this
 * wizard, `AssignDeviceDialog`, and the `/vehicles` row detail — and three
 * hand-written copies of a gate is three chances for one of them to be wrong
 * about what "ready" means. The layout below is this screen's; the rules are
 * not.
 *
 * WHAT IS ABSENT AND WHY: `5g` also draws a RELAY test and FUEL CALIBRATION.
 * Neither can run — the relay needs the bench rig slice 14 is blocked on, and
 * fuel needs an LLS probe nobody has bought. They are not rendered greyed out:
 * a disabled control promises a feature and dates the promise.
 */

type Step = 1 | 2 | 3 | 4;

/**
 * One line of the picker: the IMEI, the model, and what SIM is in the box.
 *
 * The SIM state is part of the LABEL rather than a footnote, because the choice
 * an installer is making is "which of these boxes am I holding" and the answer
 * is often decided by the sticker on the SIM.
 */
function deviceOptionLabel(d: AdminDevice, t: (k: string) => string): string {
  const parts = [d.imei];
  if (d.model) parts.push(d.model);
  if (d.simId === null) parts.push(t('provision.device.noSim'));
  else if (d.simMsisdn === null) parts.push(`${d.simIccid ?? ''} · ${t('provision.device.simNotActivated')}`);
  else parts.push(`${d.simMsisdn}${d.simCarrier ? ` · ${d.simCarrier}` : ''}`);
  return parts.join(' · ');
}

export function ProvisionWizard({ tenant, onClose }: { tenant: AdminTenant; onClose: () => void }) {
  const { t } = useTranslation();
  const [step, setStep] = useState<Step>(1);
  const [draft, setDraft] = useState<Draft>(EMPTY_DRAFT);
  const [vehicleId, setVehicleId] = useState<string | null>(null);
  const [errorKey, setErrorKey] = useState<string | null>(null);
  const [registering, setRegistering] = useState(false);

  const provision = useProvisionVehicle(tenant.id);
  const devicesQ = useDevices();

  const set = <K extends keyof Draft>(k: K, v: Draft[K]) => setDraft((d) => ({ ...d, [k]: v }));

  const allDevices = Array.isArray(devicesQ.data?.devices) ? devicesQ.data.devices : [];
  const ready = allDevices.filter(isReadyToInstall);
  const chosen = allDevices.find((d) => d.id === draft.deviceId) ?? null;
  const hasSim = chosen !== null && chosen.simId !== null;

  // 🔴 EACH STEP GATES ON ITS OWN FIELDS. The first version required the PLATE
  // to leave step 1 — and the plate is collected on step 2, so Next could never
  // enable and the wizard could not be used at all. Found by a test, not by
  // reading it back.
  const canLeaveStep: Record<number, boolean> = {
    1: draft.deviceId !== '',
    2: draft.plate.trim().length > 0,
    3: true,
  };

  async function submit() {
    setErrorKey(null);
    try {
      const res = await provision.mutateAsync(draftToBody(draft));
      setVehicleId(res.vehicleId);
      // Step 4 runs the checks the moment it opens (`autoRun`) — a freshly
      // installed device usually has not reported yet, and seeing that plainly
      // is the point.
      setStep(4);
    } catch (e) {
      // Through the shared mapper, so a 409 reads as a sentence rather than as
      // "api 409" — every provisioning code is in its closed vocabulary.
      setErrorKey(inventoryErrorKey(e));
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t('provision.title', { name: tenant.name })}
      className="fixed inset-0 z-50 flex items-stretch justify-center overflow-y-auto bg-black/60 p-0 sm:items-start sm:p-8"
    >
      <div className="w-full min-h-dvh rounded-none border-0 bg-raad-panel p-5 pt-[calc(1.25rem+var(--raad-safe-top))] sm:min-h-0 sm:max-w-2xl sm:rounded-2xl sm:border sm:border-raad-border sm:p-6 sm:pt-6">
        <div className="mb-4 flex items-start justify-between gap-4">
          <div>
            <h2 className="text-[17px] font-semibold">
              {t('provision.title', { name: tenant.name })}
            </h2>
            <p className="mt-0.5 text-[12.5px] text-raad-muted">
              {t('provision.step', { n: step, of: 4 })} · {t(`provision.stepName.${step}`)}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="min-h-9 rounded-lg border border-raad-border px-3 text-[12.5px] font-semibold"
          >
            {t('provision.close')}
          </button>
        </div>

        {step === 1 ? (
          <div className="grid gap-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="sm:col-span-2">
                <Field label={t('provision.device.label')} hint={t('provision.device.hint')}>
                  <select
                    className={INPUT}
                    value={draft.deviceId}
                    onChange={(e) => set('deviceId', e.target.value)}
                    autoFocus
                  >
                    <option value="">{t('provision.device.placeholder')}</option>
                    {ready.map((d) => (
                      <option key={d.id} value={d.id}>
                        {deviceOptionLabel(d, t)}
                      </option>
                    ))}
                  </select>
                </Field>
                {devicesQ.isPending ? (
                  <p className="mt-1 text-[11.5px] text-raad-muted">{t('inventory.loading')}</p>
                ) : ready.length === 0 ? (
                  // Said in words rather than left as an empty dropdown.
                  <p className="mt-1 text-[11.5px] text-raad-muted">
                    {t('provision.device.noneReady')}
                  </p>
                ) : null}
                {/* 🔴 THE ESCAPE HATCH. An installer standing at a truck with an
                    unregistered box otherwise has no path forward, and that is
                    precisely how people start working around the register. */}
                <button
                  type="button"
                  onClick={() => setRegistering(true)}
                  className="mt-2 min-h-9 rounded-lg border border-raad-border px-3 text-[12.5px] font-semibold transition-colors hover:bg-raad-primary/10"
                >
                  {t('provision.device.registerInline')}
                </button>
              </div>

              {chosen !== null ? (
                <div className="sm:col-span-2 grid gap-2 rounded-lg border border-raad-border p-3">
                  <p className="text-[12.5px]">
                    <span className="text-raad-muted">{t('inventory.devices.imei')}: </span>
                    <span className="num">{chosen.imei}</span>
                    {chosen.model ? <span className="text-raad-muted"> · {chosen.model}</span> : null}
                  </p>
                  <p className="text-[12.5px]">
                    <span className="text-raad-muted">{t('inventory.devices.sim')}: </span>
                    {chosen.simId === null ? (
                      <span className="text-raad-warn">{t('provision.device.noSim')}</span>
                    ) : (
                      <>
                        <span className="num">{chosen.simIccid}</span>
                        {chosen.simMsisdn === null ? (
                          <span className="text-raad-warn">
                            {' · '}
                            {t('provision.device.simNotActivated')}
                          </span>
                        ) : (
                          <span className="num"> · {chosen.simMsisdn}</span>
                        )}
                        {chosen.simCarrier ? (
                          <span className="text-raad-muted"> · {chosen.simCarrier}</span>
                        ) : null}
                      </>
                    )}
                  </p>
                  {chosen.simId !== null && chosen.simMsisdn === null ? (
                    <Caution>{t('provision.device.simNotActivatedWarning')}</Caution>
                  ) : null}
                </div>
              ) : null}
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <HardwareFields draft={draft} set={set} />
            </div>
          </div>
        ) : null}

        {step === 2 ? (
          <div className="grid gap-4 sm:grid-cols-2">
            <IdentityFields draft={draft} set={set} autoFocus />
          </div>
        ) : null}

        {step === 3 ? (
          <div className="grid gap-4">
            {/* 🔴 READ-ONLY, AND THERE IS NO INPUT TO TYPE INTO. The SIM is
                whatever is fitted to the chosen device. A typed SIM string here
                would re-create exactly the divergence this slice removed. */}
            <div className="rounded-lg border border-raad-border p-3">
              <p className="text-[12px] font-medium text-raad-muted">{t('provision.simFitted')}</p>
              {chosen === null ? (
                <p className="mt-1 text-[13px] text-raad-muted">{t('provision.device.placeholder')}</p>
              ) : chosen.simId === null ? (
                <p className="mt-1 text-[13px]">
                  <span className="text-raad-warn">{t('provision.simMissing')}</span>{' '}
                  <Link to="/devices" className="text-raad-accent underline">
                    {t('provision.simMissingLink')}
                  </Link>
                </p>
              ) : (
                <p className="mt-1 text-[13px]">
                  <span className="num">{chosen.simIccid}</span>
                  {chosen.simMsisdn === null ? (
                    <span className="text-raad-warn"> · {t('provision.device.simNotActivated')}</span>
                  ) : (
                    <span className="num"> · {chosen.simMsisdn}</span>
                  )}
                  {chosen.simCarrier ? (
                    <span className="text-raad-muted"> · {chosen.simCarrier}</span>
                  ) : null}
                </p>
              )}
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <PlanField draft={draft} set={set} />
            </div>
          </div>
        ) : null}

        {step === 4 && vehicleId !== null ? (
          <div className="grid gap-4">
            {/* 🔴 THE INSTALL RECORD, DIRECTLY ABOVE THE GATE (slice 30). The
                go-live gate's `install_confirmed` check is derived from a
                confirmed record here, so the record is captured on the same step
                that releases the vehicle. */}
            <InstallationPanel vehicleId={vehicleId} />
            {/* 🔴 NO not-live banner here — GoLivePanel says it, conditionally.
                Rendering it here too meant it survived going live and contradicted
                the panel on the same screen. See AssignDeviceDialog for the full
                note. */}
            {/* 🔴 THE SHARED GATE. Same component as `/vehicles` and
                `AssignDeviceDialog` — see `GoLivePanel.tsx`, including why its
                disabled button is not an "absence over disabled" violation. */}
            <GoLivePanel vehicleId={vehicleId} liveAt={null} autoRun />
          </div>
        ) : null}

        {errorKey ? (
          <p
            role="alert"
            className="mt-3 rounded-lg border border-raad-error/40 bg-raad-error/10 p-3 text-[12.5px]"
          >
            {t(errorKey)}
          </p>
        ) : null}

        <div className="mt-5 flex items-center justify-between gap-3">
          <button
            type="button"
            onClick={() => setStep((s) => (s > 1 ? ((s - 1) as Step) : s))}
            disabled={step === 1 || step === 4}
            className="min-h-10 rounded-lg border border-raad-border px-4 text-[13px] font-semibold disabled:opacity-40"
          >
            {t('provision.back')}
          </button>

          {step < 3 ? (
            <button
              type="button"
              onClick={() => setStep((s) => (s + 1) as Step)}
              disabled={!canLeaveStep[step]}
              className="min-h-10 rounded-lg bg-raad-primary px-4 text-[13px] font-semibold text-white disabled:opacity-40"
            >
              {t('provision.next')}
            </button>
          ) : null}

          {step === 3 ? (
            // 🔴 BLOCKED WITHOUT A SIM, and the reason is on the screen above
            // rather than only in this predicate: a tracker with no SIM cannot
            // report, so provisioning it would create a vehicle that can never
            // pass its own go-live checks.
            <button
              type="button"
              onClick={() => void submit()}
              disabled={provision.isPending || !canLeaveStep[1] || !canLeaveStep[2] || !hasSim}
              className="min-h-10 rounded-lg bg-raad-primary px-4 text-[13px] font-semibold text-white disabled:opacity-40"
            >
              {provision.isPending ? t('provision.saving') : t('provision.save')}
            </button>
          ) : null}

        </div>
      </div>

      {registering ? (
        <NewDeviceDialog
          onClose={() => setRegistering(false)}
          // Selected straight away. The register list refetches on the create's
          // invalidation, and the picker shows it as soon as it arrives.
          onCreated={(id) => set('deviceId', id)}
        />
      ) : null}
    </div>
  );
}
